import { useEffect, useState } from "react";
import { Loader2, Image as ImageIcon, Mail, MailX } from "lucide-react";
import { toast } from "sonner";
import { feedbackApi } from "@/lib/api";

const STATUS_META = {
  nowe: { label: "Nowe", cls: "border-brand-accent/40 bg-brand-accent/10 text-brand-accent" },
  w_trakcie: { label: "W trakcie", cls: "border-amber-500/40 bg-amber-500/10 text-amber-400" },
  rozwiazane: { label: "Rozwiązane", cls: "border-emerald-500/40 bg-emerald-500/10 text-emerald-400" },
};
const fmt = (v) => { try { return new Date(v).toLocaleString("pl-PL", { dateStyle: "medium", timeStyle: "short" }); } catch { return v || "—"; } };

export default function AdminFeedback() {
  const [data, setData] = useState(null);
  const [category, setCategory] = useState("");
  const [status, setStatus] = useState("");
  const [open, setOpen] = useState(null);
  const [busy, setBusy] = useState(false);

  const load = () => {
    const params = {};
    if (category) params.category = category;
    if (status) params.status = status;
    feedbackApi.adminList(params).then(setData).catch(() => toast.error("Nie udało się wczytać zgłoszeń."));
  };
  useEffect(load, [category, status]);

  const changeStatus = async (id, s) => {
    setBusy(true);
    try {
      const updated = await feedbackApi.adminSetStatus(id, s);
      setOpen(updated);
      toast.success("Status zaktualizowany.");
      load();
    } catch { toast.error("Nie udało się zmienić statusu."); }
    finally { setBusy(false); }
  };

  const cats = data?.categories || {};

  return (
    <div className="space-y-6" data-testid="admin-feedback-page">
      <div>
        <h1 className="font-display text-2xl font-semibold text-brand-text">Zgłoszenia</h1>
        <p className="text-sm text-brand-sec">Zgłoszenia „Zgłoś problem lub uwagę” od użytkowników i gości.</p>
      </div>

      <div className="flex flex-wrap items-center gap-3">
        <select data-testid="admin-feedback-filter-category" value={category} onChange={(e) => setCategory(e.target.value)} className="rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text">
          <option value="">Wszystkie kategorie</option>
          {Object.entries(cats).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select data-testid="admin-feedback-filter-status" value={status} onChange={(e) => setStatus(e.target.value)} className="rounded-field border border-brand-line bg-brand-bg px-3 py-2 text-sm text-brand-text">
          <option value="">Wszystkie statusy</option>
          <option value="nowe">Nowe</option>
          <option value="w_trakcie">W trakcie</option>
          <option value="rozwiazane">Rozwiązane</option>
        </select>
        {data?.statusCounts && (
          <div className="ml-auto flex gap-2 text-xs text-brand-muted">
            <span>Nowe: {data.statusCounts.nowe}</span>·
            <span>W trakcie: {data.statusCounts.w_trakcie}</span>·
            <span>Rozwiązane: {data.statusCounts.rozwiazane}</span>
          </div>
        )}
      </div>

      {!data ? (
        <div className="flex justify-center py-16"><Loader2 className="animate-spin text-brand-accent" /></div>
      ) : data.items.length === 0 ? (
        <div className="rounded-panel border border-brand-line bg-brand-surface p-8 text-center text-sm text-brand-sec" data-testid="admin-feedback-empty">Brak zgłoszeń dla wybranych filtrów.</div>
      ) : (
        <div className="overflow-hidden rounded-panel border border-brand-line" data-testid="admin-feedback-list">
          <table className="w-full text-left text-sm">
            <thead className="bg-brand-elevated text-xs uppercase text-brand-muted">
              <tr><th className="px-4 py-3">Kategoria</th><th className="px-4 py-3">Od</th><th className="px-4 py-3 hidden md:table-cell">Moduł</th><th className="px-4 py-3">Status</th><th className="px-4 py-3 hidden sm:table-cell">Data</th><th className="px-4 py-3"></th></tr>
            </thead>
            <tbody>
              {data.items.map((it) => (
                <tr key={it.id} data-testid={`admin-feedback-row-${it.id}`} className="border-t border-brand-line hover:bg-brand-elevated/50">
                  <td className="px-4 py-3 font-medium text-brand-text">{it.categoryLabel}</td>
                  <td className="px-4 py-3 text-brand-sec">{it.isGuest ? "Gość" : (it.userEmail || "—")}</td>
                  <td className="px-4 py-3 hidden text-brand-sec md:table-cell">{it.module || "—"}</td>
                  <td className="px-4 py-3"><span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_META[it.status]?.cls}`}>{STATUS_META[it.status]?.label}</span></td>
                  <td className="px-4 py-3 hidden text-brand-muted sm:table-cell">{fmt(it.createdAt)}</td>
                  <td className="px-4 py-3 text-right"><button data-testid={`admin-feedback-open-${it.id}`} onClick={() => setOpen(it)} className="rounded-button border border-brand-line px-3 py-1.5 text-xs font-semibold text-brand-text hover:border-brand-accent/60">Podgląd</button></td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      {open && (
        <div className="fixed inset-0 z-[70] flex items-end justify-center bg-black/60 p-0 backdrop-blur-sm sm:items-center sm:p-4" data-testid="admin-feedback-detail" onMouseDown={(e) => { if (e.target === e.currentTarget) setOpen(null); }}>
          <div className="w-full max-w-2xl overflow-y-auto rounded-t-panel border border-brand-line bg-brand-surface sm:max-h-[85vh] sm:rounded-panel">
            <div className="flex items-center justify-between border-b border-brand-line px-5 py-4">
              <div className="flex items-center gap-2">
                <h2 className="font-display text-lg font-semibold text-brand-text">{open.categoryLabel}</h2>
                <span className={`rounded-full border px-2.5 py-0.5 text-[11px] font-semibold ${STATUS_META[open.status]?.cls}`}>{STATUS_META[open.status]?.label}</span>
                {open.emailSent ? <Mail size={15} className="text-emerald-400" title="Powiadomienie wysłane" /> : <MailX size={15} className="text-brand-muted" title="Powiadomienie niewysłane (zgłoszenie zapisane)" />}
              </div>
              <button onClick={() => setOpen(null)} data-testid="admin-feedback-detail-close" className="text-brand-muted hover:text-brand-text">✕</button>
            </div>
            <div className="space-y-4 px-5 py-5 text-sm">
              <p className="whitespace-pre-wrap rounded-field border border-brand-line bg-brand-bg p-3 text-brand-text" data-testid="admin-feedback-detail-desc">{open.description}</p>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-2 text-[13px]">
                <dt className="text-brand-muted">Od</dt><dd className="text-brand-text">{open.isGuest ? "Gość" : (open.userEmail || "—")}</dd>
                <dt className="text-brand-muted">E-mail kontaktowy</dt><dd className="text-brand-text">{open.contactEmail || "—"}</dd>
                <dt className="text-brand-muted">Zgoda na kontakt</dt><dd className="text-brand-text">{open.consentContact ? "tak" : "nie"}</dd>
                <dt className="text-brand-muted">Moduł</dt><dd className="text-brand-text">{open.module || "—"}</dd>
                <dt className="text-brand-muted">Adres</dt><dd className="break-all text-brand-text">{open.url || "—"}</dd>
                <dt className="text-brand-muted">Data</dt><dd className="text-brand-text">{fmt(open.createdAt)}</dd>
                {Object.entries(open.refs || {}).map(([k, v]) => (<><dt key={k + "k"} className="text-brand-muted">{k}</dt><dd key={k + "v"} className="break-all text-brand-text">{v}</dd></>))}
                {Object.entries(open.tech || {}).map(([k, v]) => (<><dt key={k + "tk"} className="text-brand-muted">{k}</dt><dd key={k + "tv"} className="break-all text-brand-text">{v}</dd></>))}
              </dl>

              {open.hasScreenshot && (
                <div>
                  <p className="mb-2 flex items-center gap-1.5 text-[13px] font-medium text-brand-text"><ImageIcon size={15} /> Załącznik</p>
                  <img src={feedbackApi.screenshotUrl(open.id)} alt="Załącznik zgłoszenia" data-testid="admin-feedback-screenshot" className="max-h-80 w-auto rounded-field border border-brand-line" />
                </div>
              )}

              <div>
                <p className="mb-2 text-[13px] font-medium text-brand-text">Zmień status</p>
                <div className="flex flex-wrap gap-2">
                  {["nowe", "w_trakcie", "rozwiazane"].map((s) => (
                    <button key={s} disabled={busy || open.status === s} onClick={() => changeStatus(open.id, s)} data-testid={`admin-feedback-status-${s}`} className={`rounded-button px-3 py-2 text-xs font-semibold transition-colors ${open.status === s ? "bg-brand-accent text-brand-on" : "border border-brand-line text-brand-text hover:border-brand-accent/60"}`}>{STATUS_META[s].label}</button>
                  ))}
                </div>
              </div>

              <div>
                <p className="mb-1.5 text-[13px] font-medium text-brand-text">Historia statusów</p>
                <ul className="space-y-1 text-[12px] text-brand-muted" data-testid="admin-feedback-history">
                  {(open.statusHistory || []).map((h, i) => (<li key={i}>{STATUS_META[h.status]?.label || h.status} — {fmt(h.at)} {h.by ? `· ${h.by}` : ""}</li>))}
                </ul>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
